import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

type QueryParamValue = string | number | boolean | null | undefined;
type QueryParamValues = QueryParamValue | readonly QueryParamValue[];
type QueryParams = Record<string, QueryParamValues>;

export const API_ROOT = new InjectionToken<string>('API_ROOT', {
  factory: () => environment.apiRoot,
});

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private httpClient = inject(HttpClient);
  private apiRoot = inject(API_ROOT);
  
  async get<T>(path: string, query?: QueryParams) : Promise<T> {
    return firstValueFrom(this.httpClient.get<T>(this.buildUrl(path, query)));
  }

  async getBlob(pathOrUrl: string): Promise<Blob> {
    return firstValueFrom(this.httpClient.get(this.resolveUrl(pathOrUrl), {
      responseType: 'blob',
    }));
  }

  async post<TOutput, TInput>(path: string, body: TInput): Promise<TOutput> {
    return firstValueFrom(this.httpClient.post<TOutput>(`${this.apiRoot}${path}`, body));
  }

  async put<TOutput, TInput>(path: string, body: TInput): Promise<TOutput> {
    return firstValueFrom(this.httpClient.put<TOutput>(`${this.apiRoot}${path}`, body));
  }

  async delete(path: string): Promise<void> {
    await firstValueFrom(this.httpClient.delete<void>(`${this.apiRoot}${path}`));
  }

  private buildUrl(path: string, query?: QueryParams): string {
    if (!query) {
      return this.resolveUrl(path);
    }

    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(query)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item === null || item === undefined || item === '') {
            continue;
          }

          searchParams.append(key, `${item}`);
        }

        continue;
      }

      if (value === null || value === undefined || value === '') {
        continue;
      }

      searchParams.append(key, `${value}`);
    }

    const queryString = searchParams.toString();
    return queryString ? `${this.resolveUrl(path)}?${queryString}` : this.resolveUrl(path);
  }

  resolveUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) {
      return pathOrUrl;
    }

    return new URL(pathOrUrl, this.apiRoot).toString();
  }
}
